import { useState } from 'react'
import type { FormEvent } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { AlertCircle, Eye, EyeOff, Loader2, Lock, User } from 'lucide-react'
import { useAuthContext } from '@/contexts/AuthContext'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'

export function LoginPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { login } = useAuthContext()

  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [videoError, setVideoError] = useState(false)
  const [logoError, setLogoError] = useState(false)

  const basePath = (import.meta.env.BASE_URL ?? '/').replace(/\/+$/, '/')
  const heroVideoSrc = `${basePath}HERO-BANNER-RK-ECOMMERCE-AGENTES.mp4`
  const heroPosterSrc = `${basePath}astronaut.png`

  // Get the intended destination from state, or default to /en
  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/en'

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError('')
    setIsLoading(true)

    // Simulate a small delay for better UX
    await new Promise(resolve => setTimeout(resolve, 300))

    const success = await login(username, password)

    if (success) {
      navigate(from, { replace: true })
    } else {
      setError(t('login.invalid_credentials'))
      setIsLoading(false)
    }
  }

  return (
    // `dark` aqui, y es una excepcion declarada, no un descuido. Esta pantalla
    // esta SIEMPRE oscura por dos razones que se suman: va sobre un video
    // oscuro, y la ruta `/login` cuelga fuera de `LocaleLayout`
    // (`AppRouter.tsx:31`), asi que el `ThemeProvider` no la alcanza y `<html>`
    // no lleva la clase del tema. Sin esto, los tokens resolvian en claro: de
    // ahi salian los 42 colores de paleta escritos a mano -slate-950, 900, 800,
    // 700, 500, 400, 300, 100- que no eran mas que la paleta oscura copiada a
    // ojo. Declarando el ambito oscuro, los mismos tokens que usa el resto de
    // la aplicacion resuelven solos y no hay ni un color escrito a mano.
    <div className="dark relative flex min-h-dvh items-center justify-center overflow-hidden bg-background p-4">
      {/* Video Background */}
      <video
        autoPlay
        loop
        muted
        playsInline
        className="absolute inset-0 z-0 h-full w-full object-cover"
        poster={heroPosterSrc}
        onError={() => setVideoError(true)}
        aria-hidden
      >
        <source src={heroVideoSrc} type="video/mp4" />
      </video>
      {videoError && <div className="absolute inset-0 z-0 bg-background" />}

      {/* Dark overlay for better readability */}
      <div className="absolute inset-0 z-[1] bg-background/60" />

      <Card className="relative z-10 w-full max-w-md bg-card/80 backdrop-blur-xl">
        <Badge className="absolute right-4 top-4 z-20">Beta</Badge>

        <CardHeader className="space-y-1 pb-2 text-center">
          {/* Astronaut Logo */}
          <div
            className={cn(
              'mx-auto mb-4 h-16 w-16 rounded-lg',
              logoError ? 'flex items-center justify-center bg-primary' : 'overflow-hidden',
            )}
          >
            {logoError ? (
              <span className="text-2xl font-black tracking-tight text-primary-foreground">RK</span>
            ) : (
              <img
                src={heroPosterSrc}
                alt="RK Logo"
                className="h-full w-full object-cover"
                onError={() => setLogoError(true)}
              />
            )}
          </div>
          <CardTitle>{t('login.title')}</CardTitle>
          <CardDescription className="space-y-1">
            <div>{t('login.subtitle_line1')}</div>
            <div className="text-xs opacity-60">{t('login.subtitle_line2')}</div>
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="username">{t('login.username')}</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder={t('login.username_placeholder')}
                  className="pl-10"
                  required
                  autoComplete="username"
                  disabled={isLoading}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">{t('login.password')}</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('login.password_placeholder')}
                  className="pl-10 pr-10"
                  required
                  autoComplete="current-password"
                  disabled={isLoading}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-xs"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-1 top-1/2 -translate-y-1/2 text-muted-foreground"
                  aria-label={t('login.toggle_password')}
                  aria-pressed={showPassword}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff /> : <Eye />}
                </Button>
              </div>
            </div>

            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? (
                <>
                  <Loader2 className="animate-spin" />
                  {t('login.signing_in')}
                </>
              ) : (
                t('login.sign_in')
              )}
            </Button>
          </form>

          <div className="mt-6 border-t pt-4">
            <p className="text-center text-xs text-muted-foreground">{t('login.help_text')}</p>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
